import React from 'react';
import { Link } from 'gatsby';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { renderRichText } from 'gatsby-source-contentful/rich-text';

import * as S from '../components/styles.css';
import createSlug from '../utils/createSlug';
import stripHtml from '../utils/stripHtml';
import Layout from '../components/layout';
import Seo from '../components/seo';
import ConcatWords from '../utils/ConcatWords';
import formatDate from '../utils/formatDate';
import AuthorContact from '../components/authorContact';
import { stripOuterTags } from '../utils/stripHtml';
import fixWhitespace from '../utils/fixWhitespace';

const options = {
  renderMark: {
    [MARKS.BOLD]: text => <b>{text}</b>,
    [MARKS.ITALIC]: text => <i>{text}</i>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const code = node.content.filter(c => c.marks?.find(m => m.type === 'code'));
      if (code.length === node.content?.length) {
        const allCode = code.map(n => n.value).join('\n');
        const lines = allCode.split('\n');
        const lang = lines.shift();
        return (
          <pre>
            <code className={'language-' + lang}>{lines.join('\n')}</code>
          </pre>
        );
      }
      return <p>{children.map(fixWhitespace)}</p>;
    },
    [BLOCKS.HEADING_1]: (node, children) => {
      return <h1>{children.map(fixWhitespace)}</h1>;
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      return <h2>{children.map(fixWhitespace)}</h2>;
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      return <h3>{children.map(fixWhitespace)}</h3>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
      const fluid = node.data?.target?.localFile?.childImageSharp?.fluid;
      if (fluid) {
        return <img src={fluid.src} srcSet={fluid.srcSet} sizes={fluid.sizes} className="content-image" />;
      }
      return null;
    },
  },
};

const BlogPost = ({ data, pageContext }) => {
  const post = pageContext.post.node;
  const { prev, next, canonicalUrl } = pageContext;
  const image = post.image?.localFile.childImageSharp.fluid.src;

  const categoryTitles = post.categories?.map(c => c.title).join(', ');

  let pageTitle = post.title;
  if (categoryTitles) {
    pageTitle = post.title + ' - ' + categoryTitles;
  }

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      function hljsLoad() {
        if (window.hljs) {
          window.hljs.highlightAll();
        } else {
          setTimeout(hljsLoad, 200);
        }
      }
      hljsLoad();
    }
  }, []);

  return (
    <Layout>
      <Seo
        title={pageTitle}
        description={stripHtml(post.excerpt.childMarkdownRemark.html)}
        image={image}
        canonicalUrl={canonicalUrl}
      />

      <S.Author>
        Av <Link to={`/author/${createSlug(post.author.name)}/`}>{post.author.name}</Link>
      </S.Author>

      <S.Title>{post.title}</S.Title>
      <S.DateText>{formatDate(post.date)}</S.DateText>

      <S.Category>
        {post.categories?.map((cat, index, arr) => (
          <ConcatWords arrCount={arr.length} index={index} key={cat.title}>
            <Link to={`/category/${createSlug(cat.title)}/`}>{cat.title}</Link>
          </ConcatWords>
        ))}
      </S.Category>

      {!!image && (
        <S.PostImage>
          <img src={image} />
        </S.PostImage>
      )}

      <S.BlogContent className="blog-text">{renderRichText(post.content, options)}</S.BlogContent>

      <div className="author-info">
        <S.AuthorName>
          <Link to={'/author/' + createSlug(post.author.name) + '/'}>{post.author.name}</Link>
        </S.AuthorName>
        <S.AuthorImage>
          <Link to={'/author/' + createSlug(post.author.name) + '/'}>
            <img
              src={post.author.image?.localFile.childImageSharp.fixed.src}
              alt={post.author.name}
              style={{ borderRadius: '50px' }}
            />
            <span>Alla inlägg &raquo;</span>
          </Link>
        </S.AuthorImage>
        <p dangerouslySetInnerHTML={{ __html: stripOuterTags(post.author.description.childMarkdownRemark.html) }} />
        <AuthorContact email={post.author.email} />
      </div>
      <S.OtherContent>
        {prev && (
          <S.PrevNavigationPost>
            <Link to={'/' + createSlug(prev.node.title) + '/'}>&laquo; {prev.node.title}</Link>
          </S.PrevNavigationPost>
        )}

        {next && (
          <S.NextNavigationPost>
            <Link to={'/' + createSlug(next.node.title) + '/'}>{next.node.title} &raquo;</Link>
          </S.NextNavigationPost>
        )}
      </S.OtherContent>
    </Layout>
  );
};

export default BlogPost;
